import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import { mapFormFieldToNullableBooleanValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IUpdateProperty } from "@rentiohq/shared-frontend/dist/reduxV2/property";
import * as propertyActions from "@rentiohq/shared-frontend/dist/reduxV2/property/property.actions";
import * as propertySelectors from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.selectors";
import {
  AMENITY_NAMES,
  createAmenityItem,
  IUpsertPropertyAmenityItem,
  prependEANPrefix,
  PropertyAmenityDataType,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import * as propertyAmenityApi from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.api";
import {
  EExtraInfoGroup,
  EField,
  IExtraData,
} from "./schema.editPropertyExtraInfo.types";

export const isPerformingSubmitActionSelector =
  (propertyId: number) => (state: IPartialRootState) =>
    propertySelectors.isUpdating(state, propertyId);

export const performingSubmitActionErrorSelector =
  (propertyId: number) => (state: IPartialRootState) =>
    propertySelectors.updateError(state, propertyId);

/** One amenity of one group: the name it is stored under, the form field it
 * comes from, and how it is typed on the way out. */
type TAmenitySpec = [string, EField, PropertyAmenityDataType];

// Which amenities each group writes. A group not listed here writes only
// property columns. The extra tab is the one place at Rentio where the whole
// CRM field list can be filled by hand, so every name the API accepts appears
// in exactly one of these lists.
export const AMENITIES_BY_GROUP: {
  [key in EExtraInfoGroup]?: TAmenitySpec[];
} = {
  [EExtraInfoGroup.General]: [
    [AMENITY_NAMES.costsComment, EField.CostsComment, "string"],
    [AMENITY_NAMES.roomCountBedroom, EField.RoomCountBedroom, "number"],
    [AMENITY_NAMES.roomCountBathroom, EField.RoomCountBathroom, "number"],
    [AMENITY_NAMES.livingSurfaceArea, EField.LivingSurfaceArea, "number"],
    [AMENITY_NAMES.buildingFloor, EField.BuildingFloor, "number"],
    [AMENITY_NAMES.buildingFloorCount, EField.BuildingFloorCount, "number"],
  ],

  [EExtraInfoGroup.Epc]: [
    [AMENITY_NAMES.eLevel, EField.ELevel, "number"],
    [AMENITY_NAMES.kLevel, EField.KLevel, "number"],
  ],

  [EExtraInfoGroup.Exterior]: [
    [AMENITY_NAMES.groundSurfaceArea, EField.GroundSurfaceArea, "number"],
    [AMENITY_NAMES.buildingSurfaceArea, EField.BuildingSurfaceArea, "number"],
    [AMENITY_NAMES.hasGarden, EField.HasGarden, "boolean"],
    [AMENITY_NAMES.gardenOrientation, EField.GardenOrientation, "string"],
    [AMENITY_NAMES.hasTerrace, EField.HasTerrace, "boolean"],
    [AMENITY_NAMES.terraceOrientation, EField.TerraceOrientation, "string"],
    [AMENITY_NAMES.hasGardenShed, EField.HasGardenShed, "boolean"],
    [AMENITY_NAMES.hasPool, EField.HasPool, "boolean"],
    [
      AMENITY_NAMES.numberOfParkingsOutside,
      EField.NumberOfParkingsOutside,
      "number",
    ],
  ],

  [EExtraInfoGroup.Interior]: [
    [AMENITY_NAMES.numberOfToilets, EField.NumberOfToilets, "number"],
    [AMENITY_NAMES.numberOfShowerRooms, EField.NumberOfShowerRooms, "number"],
    [AMENITY_NAMES.hasAttic, EField.HasAttic, "boolean"],
    [AMENITY_NAMES.buildingHasStorage, EField.BuildingHasStorage, "boolean"],
    [AMENITY_NAMES.isFurnished, EField.IsFurnished, "boolean"],
    [AMENITY_NAMES.kitchenType, EField.KitchenType, "string"],
    [AMENITY_NAMES.floorType, EField.FloorType, "string"],
    [
      AMENITY_NAMES.buildingHasHardMaterials,
      EField.BuildingHasHardMaterials,
      "boolean",
    ],
    [AMENITY_NAMES.hasOven, EField.HasOven, "boolean"],
    [AMENITY_NAMES.hasFridge, EField.HasFridge, "boolean"],
    [AMENITY_NAMES.hasDishwasher, EField.HasDishWasher, "boolean"],
    [AMENITY_NAMES.hasRangeHood, EField.HasRangeHood, "boolean"],
    [AMENITY_NAMES.roomCountGarage, EField.RoomCountGarage, "number"],
    [
      AMENITY_NAMES.numberOfParkingsInside,
      EField.NumberOfParkingsInside,
      "number",
    ],
  ],

  [EExtraInfoGroup.Comfort]: [
    [AMENITY_NAMES.hasLift, EField.HasLift, "boolean"],
    [AMENITY_NAMES.hasAirConditioning, EField.HasAirConditioning, "boolean"],
    [AMENITY_NAMES.hasAlarm, EField.HasAlarm, "boolean"],
    [AMENITY_NAMES.hasVideophone, EField.HasVideophone, "boolean"],
    [AMENITY_NAMES.hasSmokeDetectors, EField.HasSmokeDetectors, "boolean"],
    [
      AMENITY_NAMES.accessibleForDisabled,
      EField.AccessibleForDisabled,
      "string",
    ],
  ],

  [EExtraInfoGroup.Energy]: [
    [AMENITY_NAMES.hasCentralHeating, EField.HasCentralHeating, "boolean"],
    [AMENITY_NAMES.heatingType, EField.HeatingType, "string"],
    [AMENITY_NAMES.heatingDistribution, EField.HeatingDistribution, "string"],
    [AMENITY_NAMES.hasDoubleGlazing, EField.HasDoubleGlazing, "boolean"],
    [AMENITY_NAMES.glazingType, EField.GlazingType, "string"],
    [AMENITY_NAMES.windowFrameMaterial, EField.WindowFrameMaterial, "string"],
    [AMENITY_NAMES.hasInsulation, EField.HasInsulation, "boolean"],
    [AMENITY_NAMES.hasSolarPanels, EField.HasSolarPanels, "boolean"],
    [AMENITY_NAMES.isLowEnergy, EField.IsLowEnergy, "boolean"],
    [AMENITY_NAMES.isPassiveHouse, EField.IsPassiveHouse, "boolean"],
    [AMENITY_NAMES.co2Emission, EField.Co2Emission, "number"],
  ],

  [EExtraInfoGroup.HouseRules]: [
    [AMENITY_NAMES.petsAllowed, EField.PetsAllowed, "string"],
    [AMENITY_NAMES.petsAllowedDetail, EField.PetsAllowedDetail, "string"],
    [AMENITY_NAMES.smokingAllowed, EField.SmokingAllowed, "string"],
  ],

  [EExtraInfoGroup.Condition]: [
    [AMENITY_NAMES.condition, EField.Condition, "string"],
    [AMENITY_NAMES.renovationYear, EField.RenovationYear, "number"],
    [AMENITY_NAMES.environmentType, EField.EnvironmentType, "string"],
  ],

  [EExtraInfoGroup.Structural]: [
    [AMENITY_NAMES.buildingAdjoined, EField.BuildingAdjoined, "string"],
    [AMENITY_NAMES.frontWidth, EField.FrontWidth, "number"],
    [AMENITY_NAMES.volume, EField.Volume, "number"],
    [AMENITY_NAMES.roofType, EField.RoofType, "string"],
    [AMENITY_NAMES.roofMaterial, EField.RoofMaterial, "string"],
  ],

  [EExtraInfoGroup.Risk]: [
    [AMENITY_NAMES.floodZoneGScore, EField.FloodZoneGScore, "string"],
    [AMENITY_NAMES.floodZonePScore, EField.FloodZonePScore, "string"],
    [
      AMENITY_NAMES.conformityCertificate,
      EField.ConformityCertificate,
      "string",
    ],
    [
      AMENITY_NAMES.electricityCertificate,
      EField.ElectricityCertificate,
      "string",
    ],
    [AMENITY_NAMES.asbestosCertificate, EField.AsbestosCertificate, "string"],
    [
      AMENITY_NAMES.asbestosCertificateDate,
      EField.AsbestosCertificateDate,
      "string",
    ],
  ],

  [EExtraInfoGroup.Media]: [
    [AMENITY_NAMES.virtualTourUrl, EField.VirtualTourUrl, "string"],
    [AMENITY_NAMES.videoUrl, EField.VideoUrl, "string"],
    [AMENITY_NAMES.model3dUrl, EField.Model3dUrl, "string"],
    [AMENITY_NAMES.floorPlanUrl, EField.FloorPlanUrl, "string"],
  ],
};

/** A date field hands back a Date, an amenity stores a string. */
const amenityValueForField = (
  value: any,
  dataType: PropertyAmenityDataType,
) => {
  if (dataType === "boolean") {
    return mapFormFieldToNullableBooleanValue(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return value;
};

const buildAmenityItems = (
  extraData: IExtraData,
  formData: any,
): IUpsertPropertyAmenityItem[] => {
  const { propertyAmenities, extraInfoGroup } = extraData;
  const specs = AMENITIES_BY_GROUP[extraInfoGroup] || [];

  return specs
    .map(([name, field, dataType]) =>
      createAmenityItem(
        propertyAmenities,
        name,
        amenityValueForField(formData[field], dataType),
        dataType,
      ),
    )
    .filter((item): item is IUpsertPropertyAmenityItem => item !== null);
};

/** What to store for a garden or terrace surface: the number when the answer
 * beside it is yes, nothing at all when it is no, and `undefined` - meaning
 * leave the column untouched - while the question has not been answered. */
const surfaceForAnswer = (answer: unknown, surface: unknown) => {
  if (answer === "yes") {
    return (surface as number | null | undefined) ?? null;
  }

  if (answer === "no") {
    return null;
  }

  return undefined;
};

/** The property columns a group writes, next to its amenities. Several groups
 * write both: the EPC card keeps the certificate on the property and the two
 * EPB levels as amenities, and the exterior card keeps the garden and terrace
 * surfaces as columns because contracts and documents already read them
 * there. */
const buildPropertyUpdate = (
  extraData: IExtraData,
  formData: any,
): IUpdateProperty => {
  const updateProperty: IUpdateProperty = {};

  switch (extraData.extraInfoGroup) {
    case EExtraInfoGroup.General:
      updateProperty.price = formData[EField.Price] ?? null;
      updateProperty.costs = formData[EField.Costs] ?? null;
      updateProperty.availableAtDate = formData[EField.AvailableAtDate] ?? null;
      break;

    case EExtraInfoGroup.Epc:
      updateProperty.epcLabel = formData[EEpcField.Label] ?? null;
      updateProperty.epcValue = formData[EEpcField.Value] ?? null;
      updateProperty.epcExpirationDate =
        formData[EEpcField.ExpirationDate] ?? null;
      updateProperty.epcCertificateId =
        formData[EEpcField.CertificateId] ?? null;
      break;

    case EExtraInfoGroup.Exterior: {
      // The surface only means something when the answer above it is yes, and
      // the field is hidden otherwise, so a "no" clears whatever was there. An
      // unanswered question is left alone rather than read as a no: that would
      // throw away a surface the broker was never asked about.
      const gardenSurface = surfaceForAnswer(
        formData[EField.HasGarden],
        formData[EField.BuildingGardenSurfaceArea],
      );
      if (gardenSurface !== undefined) {
        updateProperty.buildingGardenSurfaceArea = gardenSurface;
      }

      const terraceSurface = surfaceForAnswer(
        formData[EField.HasTerrace],
        formData[EField.BuildingTerraceSurfaceArea],
      );
      if (terraceSurface !== undefined) {
        updateProperty.buildingTerraceSurfaceArea = terraceSurface;
      }
      break;
    }

    case EExtraInfoGroup.Condition:
      updateProperty.constructionYear =
        formData[EField.ConstructionYear] ?? null;
      break;

    case EExtraInfoGroup.Bookkeeping:
      updateProperty.costUnit = formData[EField.CostUnit] ?? null;
      updateProperty.bookkeepingPaidOutStartDate =
        formData[EField.BookkeepingPaidOutStartDate] ?? null;
      break;

    case EExtraInfoGroup.BookkeepingGroup:
      updateProperty.costCenter = formData[EField.CostCenter] ?? null;
      break;

    case EExtraInfoGroup.Electricity:
      updateProperty.electricityType = formData[EField.ElectricityType] ?? null;
      updateProperty.electricityEan =
        prependEANPrefix(formData[EField.ElectricityEan]) ?? null;
      updateProperty.electricityMeterNumberBoth =
        formData[EField.ElectricityMeterNumberBoth] ?? null;
      updateProperty.electricityMeterType =
        formData[EField.ElectricityMeterType] ?? null;
      break;

    case EExtraInfoGroup.NaturalGas:
      updateProperty.naturalGasAvailable =
        formData[EField.NaturalGasAvailable] ?? null;
      updateProperty.naturalGasEan =
        prependEANPrefix(formData[EField.NaturalGasEan]) ?? null;
      updateProperty.naturalGasMeterNumber =
        formData[EField.NaturalGasMeterNumber] ?? null;
      updateProperty.naturalGasMeterType =
        formData[EField.NaturalGasMeterType] ?? null;
      break;

    case EExtraInfoGroup.Water:
      updateProperty.waterMeterNumber =
        formData[EField.WaterMeterNumber] ?? null;
      break;

    default:
      break;
  }

  return updateProperty;
};

export const submitActionCreator =
  (extraData: IExtraData) => (formData: any) => {
    const { property } = extraData;

    const amenityItems = buildAmenityItems(extraData, formData);
    if (amenityItems.length > 0) {
      propertyAmenityApi.upsertForProperty(property.id, {
        amenities: amenityItems,
      });
    }

    // A group that writes no property column still dispatches the update, with
    // an empty body: that action is what tells the form it succeeded and closes
    // the modal.
    return propertyActions.updateStart.getAction({
      id: property.id,
      data: buildPropertyUpdate(extraData, formData),
    });
  };
